import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const api = {
  // auth
  me: () => client.get("/auth/me").then((r) => r.data),
  session: (session_id, affiliate_code = "") =>
    client.post("/auth/session", { session_id, affiliate_code }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),

  // onboarding
  onboarding: (brandId) => client.get(`/onboarding/state${brandId ? `?brand_id=${brandId}` : ""}`).then((r) => r.data),
  updateOnboarding: (data, brandId) => client.put(`/onboarding/state${brandId ? `?brand_id=${brandId}` : ""}`, data).then((r) => r.data),

  // brands
  listBrands: () => client.get("/brands").then((r) => r.data),
  createBrand: (data) => client.post("/brands", data).then((r) => r.data),
  getBrand: (id) => client.get(`/brands/${id}`).then((r) => r.data),
  updateBrand: (id, data) => client.put(`/brands/${id}`, data).then((r) => r.data),
  deleteBrand: (id) => client.delete(`/brands/${id}`).then((r) => r.data),
  listConnections: (id) => client.get(`/brands/${id}/connections`).then((r) => r.data),
  addConnection: (id, data) => client.post(`/brands/${id}/connections`, data).then((r) => r.data),
  removeConnection: (id, cid) => client.delete(`/brands/${id}/connections/${cid}`).then((r) => r.data),

  // interview
  startInterview: (brand_id, fresh = false) => client.post("/interview/start", { brand_id, fresh }).then((r) => r.data),
  answerInterview: (sid, answer) => client.post(`/interview/${sid}/answer`, { answer }).then((r) => r.data),
  getInterview: (sid) => client.get(`/interview/${sid}`).then((r) => r.data),
  interviewHistory: (brand_id) => client.get(`/interview/brand/${brand_id}/history`).then((r) => r.data),

  // knowledge
  uploadKnowledge: (brand_id, file, onProgress) => {
    const fd = new FormData();
    fd.append("brand_id", brand_id);
    fd.append("file", file);
    return client.post("/knowledge/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    }).then((r) => r.data);
  },
  listKnowledge: (brand_id) => client.get(`/knowledge/brand/${brand_id}`).then((r) => r.data),
  deleteKnowledge: (id) => client.delete(`/knowledge/${id}`).then((r) => r.data),

  // flags
  listFlags: (status = "pending") => client.get(`/flags?status=${status}`).then((r) => r.data),
  confirmFlag: (id, action) => client.post(`/flags/${id}/confirm`, { action }).then((r) => r.data),

  // content
  generateContent: (data) => client.post("/content/generate", data).then((r) => r.data),
  generateYear: (brandId, data) => client.post(`/brands/${brandId}/generate-year`, data).then((r) => r.data),
  refreshPending: (brandId) => client.post(`/brands/${brandId}/refresh-pending`).then((r) => r.data),
  staleCount: (brandId) => client.get(`/brands/${brandId}/stale-count`).then((r) => r.data),
  getJob: (id) => client.get(`/jobs/${id}`).then((r) => r.data),
  listContent: (brand_id, status) => client.get(`/content/brand/${brand_id}${status ? `?status=${status}` : ""}`).then((r) => r.data),
  getPost: (id) => client.get(`/content/${id}`).then((r) => r.data),
  approvePost: (id, data = {}) => client.put(`/content/${id}/approve`, data).then((r) => r.data),
  rejectPost: (id, notes) => client.put(`/content/${id}/reject`, { notes }).then((r) => r.data),
  editPost: (id, data) => client.put(`/content/${id}/edit`, data).then((r) => r.data),
  setPostTitle: (id, title) => client.put(`/content/${id}/set-title`, { title }).then((r) => r.data),
  publishNow: (id) => client.post(`/content/${id}/publish-now`).then((r) => r.data),
  draftToSubstack: (id) => client.post(`/content/${id}/draft-to-substack`).then((r) => r.data),
  // Substack Connect (magic-link → encrypted session cookie → auto-draft)
  substackStatus: (brandId) => client.get(`/brands/${brandId}/substack/status`).then((r) => r.data),
  substackRequestLink: (brandId, login_email, publication_url) =>
    client.post(`/brands/${brandId}/substack/request-link`, { login_email, publication_url }).then((r) => r.data),
  substackExchange: (brandId, payload) =>
    client.post(`/brands/${brandId}/substack/exchange`, payload).then((r) => r.data),
  substackDisconnect: (brandId) => client.delete(`/brands/${brandId}/substack`).then((r) => r.data),
  renderedHtml: (id) => client.get(`/content/${id}/rendered-html`).then((r) => r.data),
  markPublished: (id, published_at = null) => client.post(`/content/${id}/mark-published`, published_at ? { published_at } : {}).then((r) => r.data),
  deletePost: (id) => client.delete(`/content/${id}`).then((r) => r.data),
  // typed/pasted knowledge + reject-note → knowledge
  addTextKnowledge: (brand_id, title, content) => client.post(`/knowledge/text`, { brand_id, title, content }).then((r) => r.data),
  addKnowledgeFromSuggestion: (payload) => client.post(`/knowledge/from-suggestion`, payload).then((r) => r.data),

  // cover images
  generateCover: (id, options = {}) => client.post(`/content/${id}/cover-image`, options).then((r) => r.data),
  listCoverStyles: () => client.get(`/cover-styles`).then((r) => r.data),
  uploadCover: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/content/${id}/cover-image/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  deleteCover: (id) => client.delete(`/content/${id}/cover-image`).then((r) => r.data),

  // analytics
  addAnalytics: (id, data) => client.post(`/content/${id}/analytics`, data).then((r) => r.data),
  listAnalytics: (id) => client.get(`/content/${id}/analytics`).then((r) => r.data),

  // playbooks
  listPlaybooks: () => client.get("/playbooks").then((r) => r.data),

  // platforms
  listPlatforms: () => client.get("/platforms").then((r) => r.data),
  listBrandPlatforms: (brandId) => client.get(`/brands/${brandId}/platforms`).then((r) => r.data),
  updateBrandPlatform: (brandId, platformId, data) => client.put(`/brands/${brandId}/platforms/${platformId}`, data).then((r) => r.data),
  adaptPostToPlatforms: (postId, platform_ids) => client.post(`/content/${postId}/adapt`, { platform_ids, count: 1, cadence: "weekly" }).then((r) => r.data),
  listPlatformPosts: (brandId, platformId) => client.get(`/platform-posts/brand/${brandId}${platformId ? `?platform_id=${platformId}` : ""}`).then((r) => r.data),
  deletePlatformPost: (id) => client.delete(`/platform-posts/${id}`).then((r) => r.data),

  // video repurpose / upload notes
  repurposeVideo: (knowledgeId) => client.post(`/knowledge/${knowledgeId}/repurpose-video`).then((r) => r.data),
  addUploadNote: (knowledgeId, note, desired_next) => client.put(`/knowledge/${knowledgeId}/note`, { note, desired_next }).then((r) => r.data),

  // workspaces
  listWorkspaces: () => client.get("/workspaces").then((r) => r.data),
  createWorkspace: (name) => client.post("/workspaces", { name }).then((r) => r.data),
  updateWorkspace: (id, data) => client.put(`/workspaces/${id}`, data).then((r) => r.data),
  workspaceMembers: (id) => client.get(`/workspaces/${id}/members`).then((r) => r.data),
  inviteMember: (id, email, role = "editor") => client.post(`/workspaces/${id}/invite`, { email, role }).then((r) => r.data),
  acceptInvite: (token) => client.post(`/workspaces/accept-invite/${token}`).then((r) => r.data),
  removeMember: (workspaceId, userId) => client.delete(`/workspaces/${workspaceId}/members/${userId}`).then((r) => r.data),

  // ayrshare (multi-platform auto-posting)
  ayrshareStatus: (workspaceId) => client.get(`/workspaces/${workspaceId}/ayrshare/status`).then((r) => r.data),
  ayrshareConnect: (workspaceId, api_key) => client.post(`/workspaces/${workspaceId}/ayrshare/connect`, { api_key }).then((r) => r.data),
  ayrshareDisconnect: (workspaceId) => client.delete(`/workspaces/${workspaceId}/ayrshare/connect`).then((r) => r.data),
  setProfileKey: (brandId, platformId, profile_key) => client.put(`/brands/${brandId}/platforms/${platformId}/profile-key`, { profile_key }).then((r) => r.data),
  autoPublishAll: (postId) => client.post(`/content/${postId}/auto-publish-all`).then((r) => r.data),

  // calendar / dashboard
  calendar: () => client.get("/calendar").then((r) => r.data),
  dashboardStats: () => client.get("/dashboard/stats").then((r) => r.data),

  // system limits + integration status (transparency dashboard)
  systemLimits: () => client.get("/system/limits").then((r) => r.data),

  // uploads with progress callback
  uploadKnowledge: (brandId, file, onProgress) => {
    const form = new FormData();
    form.append("brand_id", brandId);
    form.append("file", file);
    return client.post("/knowledge/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
      },
      timeout: 300000,
    }).then((r) => r.data);
  },

  // AI subject variants (A/B subject line testing)
  subjectVariants: (post_id) => client.post(`/content/${post_id}/subject-variants`).then((r) => r.data),
  updatePostSubject: (post_id, subject) => client.put(`/content/${post_id}/subject`, { subject }).then((r) => r.data),
  abSplitSend: (post_id, payload) => client.post(`/content/${post_id}/ab-split-send`, payload).then((r) => r.data),
  abStatus: (post_id) => client.get(`/content/${post_id}/ab-status`).then((r) => r.data),

  // Launch Day wizard
  launchDayStatus: () => client.get("/launch-day/status").then((r) => r.data),
  launchDayLink: (payload) => client.post("/launch-day/link", payload).then((r) => r.data),
  launchDayGenerateFirstPost: () => client.post("/launch-day/generate-first-post").then((r) => r.data),
  launchDayComplete: () => client.post("/launch-day/complete").then((r) => r.data),

  // Mogul Marketplace (public discoverability)
  mogulMarketplace: (niche = "", sort = "leads") => client.get(`/mogul/marketplace?niche=${encodeURIComponent(niche)}&sort=${sort}`).then((r) => r.data),

  // Mogul Mode (public profile)
  getMyMogulProfile: () => client.get("/mogul/profile/me").then((r) => r.data),
  saveMogulProfile: (payload) => client.put("/mogul/profile/me", payload).then((r) => r.data),
  checkMogulSlug: (slug) => client.get(`/mogul/check-slug/${slug}`).then((r) => r.data),
  publicMogulProfile: (slug) => client.get(`/mogul/public/${slug}`).then((r) => r.data),
  mogulByDomain: (domain) => client.get(`/mogul/by-domain/${domain}`).then((r) => r.data),
  mogulSpotlight: () => client.get("/mogul/spotlight").then((r) => r.data),
  draftMogulCopy: (services, style_hint = "") => client.post("/mogul/profile/draft-copy", { services, style_hint }).then((r) => r.data),
  submitMogulContact: (slug, payload) => client.post(`/mogul/public/${slug}/contact`, payload).then((r) => r.data),
  listMogulLeads: () => client.get("/mogul/leads").then((r) => r.data),

  // Sponsorship marketplace
  saveSponsorshipListing: (payload) => client.put("/sponsorships/listing", payload).then((r) => r.data),
  myListings: () => client.get("/sponsorships/my-listings").then((r) => r.data),
  deactivateListing: (id) => client.delete(`/sponsorships/listing/${id}`).then((r) => r.data),
  browseMarketplace: () => client.get("/sponsorships/marketplace").then((r) => r.data),
  bookSponsorship: (payload) => client.post("/sponsorships/book", payload).then((r) => r.data),
  myBookings: () => client.get("/sponsorships/my-bookings").then((r) => r.data),
  inboundBookings: () => client.get("/sponsorships/inbound").then((r) => r.data),
  decideSponsorship: (id, decision, note = "") => client.post(`/sponsorships/booking/${id}/decide`, { decision, note }).then((r) => r.data),

  // White-label custom domain
  addWhitelabelDomain: (workspace_id, domain) => client.post("/whitelabel/domain", { workspace_id, domain }).then((r) => r.data),
  getWhitelabelDomain: (workspace_id) => client.get(`/whitelabel/domain/${workspace_id}`).then((r) => r.data),
  verifyWhitelabelDomain: (workspace_id) => client.post(`/whitelabel/domain/${workspace_id}/verify`).then((r) => r.data),
  deleteWhitelabelDomain: (workspace_id) => client.delete(`/whitelabel/domain/${workspace_id}`).then((r) => r.data),

  // podcast / Buzzsprout
  buzzsproutConfig: (workspace_id) => client.get(`/podcast/buzzsprout/config/${workspace_id}`).then((r) => r.data),
  saveBuzzsprout: (workspace_id, podcast_id, api_token) =>
    client.put("/podcast/buzzsprout/config", { workspace_id, podcast_id, api_token }).then((r) => r.data),
  publishEpisode: (workspace_id, episode) =>
    client.post("/podcast/buzzsprout/publish", { workspace_id, ...episode }).then((r) => r.data),
  listEpisodes: (workspace_id) => client.get(`/podcast/episodes/${workspace_id}`).then((r) => r.data),

  // billing / pricing
  billingConfig: () => client.get("/billing/config").then((r) => r.data),
  billingMe: () => client.get("/billing/me").then((r) => r.data),
  createCheckout: (tier, interval, originUrl, coupon) =>
    client.post("/billing/checkout", { tier, interval, origin_url: originUrl, coupon }).then((r) => r.data),
  checkoutStatus: (sessionId) => client.get(`/billing/checkout/status/${sessionId}`).then((r) => r.data),
  billingPortal: (originUrl) => client.post("/billing/portal", { origin_url: originUrl }).then((r) => r.data),
  cancelSub: () => client.post("/billing/cancel").then((r) => r.data),
  // trial
  trialEligibility: () => client.get("/billing/trial/eligibility").then((r) => r.data),
  startTrial: () => client.post("/billing/trial/start").then((r) => r.data),
  // founders wall (public)
  foundersWall: () => client.get("/billing/founders-wall").then((r) => r.data),

  // affiliate
  affiliateMe: () => client.get("/affiliate/me").then((r) => r.data),
  updateAffiliate: (payout_email, payout_method = "manual") =>
    client.put("/affiliate/me", { payout_email, payout_method }).then((r) => r.data),
  affiliateReferrals: () => client.get("/affiliate/referrals").then((r) => r.data),
  affiliateCredits: () => client.get("/affiliate/credits").then((r) => r.data),
  requestPayout: () => client.post("/affiliate/request-payout").then((r) => r.data),
  trackAffiliateClick: (code) => client.post("/affiliate/track-click", { code }),
  attributeAffiliate: (code) => client.post("/affiliate/attribute", { code }),
  // payout preferences
  updatePayoutPrefs: (auto_payout_preference, auto_payout_threshold_cents = 10000) =>
    client.put("/affiliate/payout-prefs", { auto_payout_preference, auto_payout_threshold_cents }).then((r) => r.data),
  // Stripe Connect Express
  connectOnboard: () => client.post("/affiliate/connect/onboard").then((r) => r.data),
  connectStatus: () => client.get("/affiliate/connect/status").then((r) => r.data),
  connectDisconnect: () => client.post("/affiliate/connect/disconnect").then((r) => r.data),
  connectPublishableKey: () => client.get("/billing/publishable-key").then((r) => r.data),
  // leaderboard + contest
  affiliateLeaderboard: () => client.get("/affiliate/leaderboard").then((r) => r.data),
  affiliateContest: () => client.get("/affiliate/contest").then((r) => r.data),
  // Substack-strategy-aware generation
  generateNotesAndRestacks: (brandId, payload = {}) =>
    client.post(`/brands/${brandId}/generate-notes-and-restacks`, payload).then((r) => r.data),
  listNotesAndRestacks: (brandId) =>
    client.get(`/brands/${brandId}/notes-and-restacks`).then((r) => r.data),
  // Strategy reference docs (Substack growth foundation, etc.)
  listStrategyDocs: () => client.get(`/strategy`).then((r) => r.data),
  getStrategyDoc: (slug) => client.get(`/strategy/${slug}`).then((r) => r.data),
  updateStrategyDoc: (slug, payload) => client.put(`/strategy/${slug}`, payload).then((r) => r.data),
};

export default api;
